import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";

const GREETING = {
  role: "casey",
  text: "Hey, Casey here from Creator Support. I can help with anything about the Creator Forge — activation, the kit, bounties, QA, payouts. What's on your mind?",
};

export default function CreatorSupportChat({ testid = "creator-support-chat" }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sp_casey_msgs") || "null") || [GREETING];
    } catch {
      return [GREETING];
    }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    localStorage.setItem("sp_casey_msgs", JSON.stringify(messages.slice(-40)));
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "me", text }]);
    setBusy(true);
    try {
      const r = await api.post("/creators/support/chat", {
        message: text,
        session_id: localStorage.getItem("sp_casey_sid") || "",
      });
      localStorage.setItem("sp_casey_sid", r.data.session_id);
      setMessages((m) => [...m, { role: "casey", text: r.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "casey", text: "Sorry — I dropped for a second there. Ask me again?" }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid} className="fixed bottom-4 left-4 z-50 flex flex-col items-start gap-3">
      {open && (
        <div data-testid="casey-panel" className="w-[330px] sm:w-[370px] border border-nc-blue/40 bg-[#070707] shadow-2xl shadow-black flex flex-col" style={{ height: "min(520px, 70vh)" }}>
          <div className="border-b border-white/10 px-4 py-3 flex items-center gap-3 bg-black/60">
            <div className="relative">
              <div className="w-9 h-9 rounded-full bg-nc-blue/20 border border-nc-blue/50 flex items-center justify-center font-sans font-bold text-sm text-nc-blue">C</div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-nc-green border-2 border-[#070707]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans font-bold text-xs text-zinc-100">Casey — Creator Support</p>
              <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em]">AI assistant · online · replies instantly</p>
            </div>
            <button data-testid="casey-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-100 transition-colors"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "me" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 font-mono text-[11px] leading-relaxed ${m.role === "me" ? "bg-nc-blue text-white" : "bg-white/5 border border-white/10 text-zinc-200"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div data-testid="casey-typing" className="flex items-center gap-1.5 px-3.5 py-2.5 bg-white/5 border border-white/10 w-fit">
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-white/10 p-3 flex gap-2">
            <input
              data-testid="casey-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask Casey anything…"
              maxLength={1000}
              className="flex-1 bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue"
            />
            <button data-testid="casey-send" onClick={send} disabled={busy || !input.trim()} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-3.5 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
      <button
        data-testid="casey-open"
        onClick={() => setOpen((o) => !o)}
        className="w-12 h-12 rounded-full bg-nc-blue hover:bg-nc-blue/80 text-white flex items-center justify-center shadow-lg shadow-nc-blue/30 transition-all hover:scale-105"
      >
        <MessageCircle className="w-5 h-5" />
      </button>
    </div>
  );
}
